import { captionTheme } from '../../statistics/lib/caption-theme';
import type { SeedingComment } from './pick-comments';

/**
 * Посев — один ролик, разложенный по нескольким пабликам. В отчёт он идёт
 * одним блоком: заголовок, все ссылки и сумма цифр по копиям.
 *
 * В ленте Instagram копии ничем не связаны: у каждой свой id, свой шорткод и
 * свой аккаунт. Связывает их подпись — при посеве её копируют дословно, иногда
 * дописывая свои хэштеги и подпись паблика.
 *
 * Сравнивается подпись целиком, а не первый абзац: часть пабликов ставит перед
 * новостью свою строку-зазывалку («Сеул аспанында таңғаларлық жайт орын алды»),
 * и по первым абзацам такие копии расходятся, хотя дальше текст тот же.
 *
 * Совпадение считается по словам, а не посимвольно: у одного паблика в начале
 * «‼️», у другого «❗️», у третьего лишний пробел — посимвольное сравнение на
 * этом разъезжается, набор слов не меняется.
 *
 * Время работает в обе стороны. Окно в двое суток — страховка от ложного
 * склеивания: рубрики повторяются («Курс валют на сегодня»), и без него разные
 * дни собрались бы в один посев. Узкое окно в двадцать минут, наоборот,
 * собирает вместе переводы одной новости — см. mergeByTime.
 */

/** Доля общих слов, начиная с которой подписи считаются одной публикацией. */
const SIMILARITY = 0.6;
/** Посев расходится по пабликам за часы; сутки с запасом — двое. */
const WINDOW_MS = 48 * 60 * 60 * 1000;
/**
 * Окно для склейки по времени — им собираются вместе переводы (см. mergeByTime).
 * Замер на посевах 16–22.09.2026: копии одного ролика расходятся по сетке за
 * 5–15 минут, соседняя новость в тот же день ушла через два часа.
 */
const TRANSLATION_WINDOW_MS = 20 * 60 * 1000;
/** Длиннее — заголовок режется по границе предложения. */
const HEADLINE_LIMIT = 160;
/** Короче куска не оставляем: обрывок в одно слово хуже длинного заголовка. */
const HEADLINE_MIN = 40;

export type SeedingItem = {
  /** Есть только у роликов TikTok: комментарии к ним читаются другой ручкой. */
  platform?: 'tiktok';
  /** Ссылка как её показывать в отчёте. */
  url: string;
  shortcode: string;
  /** id медиа в Graph API — по нему запрашиваются комментарии. */
  mediaId: string;
  /** Ник паблика без @. */
  username: string;
  caption: string | null;
  publishedAt: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  /** Репосты у Instagram расчётные, поэтому бывают неизвестны. */
  reposts: number | null;
  /**
   * Обложка на CDN Instagram: у рилса — thumbnail_url, у фото и карусели —
   * сама картинка. Адрес подписанный и живёт около суток, в документ он не
   * идёт — его перезаливает attach-covers.
   */
  coverSource?: string | null;
};

export type SeedingTotals = {
  views: number;
  likes: number;
  comments: number;
  /** null — ни по одной копии репостов не посчиталось; это не ноль. */
  reposts: number | null;
};

export type SeedingGroup = {
  /** Тема блока: первый абзац подписи самой просматриваемой копии. */
  headline: string;
  items: SeedingItem[];
  totals: SeedingTotals;
  /**
   * Отобранные комментарии по всем копиям посева. Поля нет — комментарии не
   * запрашивали; пустой список — запрашивали, но в отчёт ничего не прошло.
   */
  comments?: SeedingComment[];
  /**
   * Обложка ролика на постоянном адресе (см. attach-covers). Поля нет или
   * null — блок идёт без картинки.
   */
  cover?: string | null;
};

/** Слова подписи в нижнем регистре: без знаков, эмодзи и коротких хвостов. */
function words(theme: string): Set<string> {
  const cleaned = theme
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, ' ')
    // Буквы и цифры любых алфавитов — казахский текст должен пережить чистку.
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return new Set(cleaned ? cleaned.split(' ') : []);
}

/** Доля общих слов (Жаккар). Пустая подпись ни с чем не совпадает. */
export function similarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let common = 0;
  for (const word of a) if (b.has(word)) common += 1;
  return common / (a.size + b.size - common);
}

function time(item: SeedingItem): number | null {
  const ms = item.publishedAt ? Date.parse(item.publishedAt) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

/** Дата неизвестна хотя бы у одной копии — окно не проверяем, решает подпись. */
function withinWindow(a: SeedingItem, b: SeedingItem): boolean {
  const at = time(a);
  const bt = time(b);
  if (at === null || bt === null) return true;
  return Math.abs(at - bt) <= WINDOW_MS;
}

/**
 * Заголовок блока.
 *
 * У половины пабликов первый абзац подписи и есть заголовок новости («Мбаппе,
 * Роналду, Беллингем… А Токаев напомнил о Сатпаеве») — он идёт целиком. У
 * остальных абзац длинный, и тогда берётся первое предложение: именно так
 * заголовки обрезаны в образце отчёта, вплоть до обрыва внутри кавычек —
 * «Над Сеулом пролетел дирижабль с надписью «Казахстан — Корея.»
 */
export function seedingHeadline(theme: string): string {
  const text = theme.trim();
  if (text.length <= HEADLINE_LIMIT) return text;
  const sentence = /[.!?…](\s|$)/.exec(text);
  if (sentence && sentence.index + 1 >= HEADLINE_MIN) {
    return text.slice(0, sentence.index + 1).trim();
  }
  // Предложение не кончилось — режем по слову, чтобы не обрывать посередине.
  const head = text.slice(0, HEADLINE_LIMIT);
  const space = head.lastIndexOf(' ');
  return `${(space >= HEADLINE_MIN ? head.slice(0, space) : head).trim()}…`;
}

function sum(values: Array<number | null>): number {
  return values.reduce((acc: number, v) => acc + (v ?? 0), 0);
}

function totals(items: SeedingItem[]): SeedingTotals {
  const reposts = items.map((i) => i.reposts);
  return {
    views: sum(items.map((i) => i.views)),
    likes: sum(items.map((i) => i.likes)),
    comments: sum(items.map((i) => i.comments)),
    reposts: reposts.every((r) => r === null) ? null : sum(reposts),
  };
}

/**
 * Посев из готового набора копий: так собираются блоки, которые оператор
 * разложил руками. Тема — его, если написана; иначе из подписи, как обычно.
 */
export function makeSeedingGroup(items: SeedingItem[], theme = ''): SeedingGroup {
  return { headline: theme.trim() || headlineOf(items), items, totals: totals(items) };
}

/** Самая просматриваемая копия — её подпись и становится заголовком. */
function headlineOf(items: SeedingItem[]): string {
  const byViews = [...items].sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
  for (const item of byViews) {
    const theme = captionTheme(item.caption);
    if (theme) return seedingHeadline(theme);
  }
  return '';
}

/**
 * Копии одного ролика — в один посев.
 *
 * Копия подсаживается к посеву, если похожа хоть на одну уже вошедшую в него
 * подпись (а не на первую): при посеве паблики правят подпись каждый по-своему,
 * и цепочка «дословная копия → копия с правкой → копия с другой правкой»
 * остаётся одним роликом.
 *
 * Порядок ссылок внутри посева — как во вводе: их обычно вставляют списком, где
 * первыми идут крупные паблики.
 */
export function groupSeeding(items: SeedingItem[]): SeedingGroup[] {
  const groups: Array<{ items: SeedingItem[]; words: Array<Set<string>> }> = [];

  for (const item of items) {
    const itemWords = words(item.caption ?? '');
    const hit = groups.find((group) =>
      group.items.some(
        (member, i) => similarity(itemWords, group.words[i]) >= SIMILARITY && withinWindow(item, member),
      ),
    );
    if (hit) {
      hit.items.push(item);
      hit.words.push(itemWords);
    } else {
      groups.push({ items: [item], words: [itemWords] });
    }
  }

  return mergeByTime(groups.map((group) => group.items)).map((group) => ({
    headline: headlineOf(group),
    items: group,
    totals: totals(group),
  }));
}

/** Ближайшая пара публикаций двух посевов по времени. null — даты неизвестны. */
function nearestGap(a: SeedingItem[], b: SeedingItem[]): number | null {
  let best = Infinity;
  for (const x of a) {
    const xt = time(x);
    if (xt === null) continue;
    for (const y of b) {
      const yt = time(y);
      if (yt !== null) best = Math.min(best, Math.abs(xt - yt));
    }
  }
  return Number.isFinite(best) ? best : null;
}

/** Ни один паблик не встречается в обоих посевах. */
function disjointAccounts(a: SeedingItem[], b: SeedingItem[]): boolean {
  const used = new Set(a.map((i) => i.username.toLowerCase()));
  return b.every((i) => !used.has(i.username.toLowerCase()));
}

/**
 * Второй проход: переводы одного ролика — в один посев.
 *
 * Русская и казахская версии новости — это один посев, но по словам они не
 * сходятся никогда: разные языки, общих слов почти нет. Их связывает то, что
 * сетка выкладывает их вместе — в образце отчёта шесть копий «дирижабля» ушли
 * с 09:28 до 09:34, а следующая новость того же дня — только в 11:31.
 *
 * Поэтому посевы склеиваются, если публикации разошлись в пределах окна И в
 * них нет общего паблика: одну и ту же новость паблик выкладывает один раз, а
 * значит пересечение по пабликам означает, что это разные ролики, как бы
 * близко по времени они ни вышли.
 */
function mergeByTime(groups: SeedingItem[][]): SeedingItem[][] {
  const merged = groups.map((g) => [...g]);
  for (let i = 0; i < merged.length; i++) {
    for (let j = i + 1; j < merged.length; j++) {
      const gap = nearestGap(merged[i], merged[j]);
      if (gap === null || gap > TRANSLATION_WINDOW_MS) continue;
      if (!disjointAccounts(merged[i], merged[j])) continue;
      merged[i].push(...merged[j]);
      merged.splice(j, 1);
      // Склеенный посев мог стать ближе к следующим — проверяем их заново.
      j = i;
    }
  }
  return merged;
}

/**
 * Топ посевов по суммарным просмотрам. При равенстве выше свежий — как в
 * «Топ видео»: то, что ещё набирает, интереснее набравшего столько же давно.
 */
export function pickTopSeeding(groups: SeedingGroup[], count: number): SeedingGroup[] {
  if (count <= 0) return [];
  const latest = (group: SeedingGroup) =>
    Math.max(...group.items.map((i) => time(i) ?? 0), 0);
  return [...groups]
    .sort((a, b) => b.totals.views - a.totals.views || latest(b) - latest(a))
    .slice(0, count);
}
